import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

export default function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="py-10">Loading…</div>;

  const handicapData = s.handicap_history.map((h, i) => ({
    i: i + 1, date: new Date(h.played_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    handicap: h.handicap_index, differential: h.differential, course: h.course,
  }));
  const scoreData = s.score_history.map((r, i) => ({
    i: i + 1, date: new Date(r.played_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    score: r.score, par: r.par, course: r.course,
  }));

  return (
    <div className="space-y-6">
      <div className="rise">
        <span className="label-eyebrow">Stats dashboard</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Your game, in numbers.</h1>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link to="/stats/advanced" data-testid="advanced-stats-link" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0F2537] text-white text-xs font-bold hover:bg-[#0F2537]/90">
            Advanced analytics →
          </Link>
          <Link to="/stats/clubs" data-testid="club-bag-link" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#E2E0D8] text-[#0F2537] text-xs font-bold hover:bg-black/5">
            Club bag →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Rounds" v={s.rounds} />
        <Tile label="Avg score" v={s.avg_score ?? "—"} />
        <Tile label="Best" v={s.best_score ?? "—"} />
        <Tile label="Avg differential" v={s.avg_diff ?? "—"} />
        <Tile label="Fairways" v={s.fairway_pct == null ? "—" : `${s.fairway_pct}%`} />
        <Tile label="GIR" v={s.gir_pct == null ? "—" : `${s.gir_pct}%`} />
        <Tile label="Putts/round" v={s.avg_putts ?? "—"} />
        <Tile label="Latest index" v={handicapData.length ? (handicapData[handicapData.length - 1].handicap ?? "—") : "—"} accent />
      </div>

      <div className="card-default p-5 md:p-7">
        <span className="label-eyebrow">WHS Index progression</span>
        <h2 className="font-heading font-bold text-xl mt-1 mb-4">Your handicap journey</h2>
        {handicapData.length === 0 ? (
          <div className="py-10 text-center text-[#0F2537]/60">Record some rounds to see your trend.</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={handicapData} margin={{ top: 5, right: 12, left: -10, bottom: 5 }}>
                <CartesianGrid stroke="#E2E0D8" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#8C949D" fontSize={11} />
                <YAxis stroke="#8C949D" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E0D8" }} />
                <Line type="monotone" dataKey="handicap" stroke="#0F2537" strokeWidth={3} dot={{ r: 4, fill: "#E05D3A" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="card-default p-5 md:p-7">
        <span className="label-eyebrow">Score history</span>
        <h2 className="font-heading font-bold text-xl mt-1 mb-4">Gross scores</h2>
        {scoreData.length === 0 ? (
          <div className="py-10 text-center text-[#0F2537]/60">No data yet.</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={scoreData} margin={{ top: 5, right: 12, left: -10, bottom: 5 }}>
                <CartesianGrid stroke="#E2E0D8" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#8C949D" fontSize={11} />
                <YAxis stroke="#8C949D" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E0D8" }} />
                <Bar dataKey="score" fill="#E05D3A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, v, accent }) {
  return (
    <div className={`card-default p-5 ${accent ? "bg-[#0F2537] text-white border-[#0F2537]" : ""}`} data-testid={`stat-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <span className={`label-eyebrow ${accent ? "!text-white/70" : ""}`}>{label}</span>
      <div className="font-heading font-black text-2xl mt-1">{v}</div>
    </div>
  );
}
